import { HttpErrorResponse } from '@angular/common/http';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
} from '@ngx-translate/core';
import { Observable, catchError, of } from 'rxjs';

import { UtilsApiService } from '../api/services/utils-api.service';
import { Injectable, Injector, signal, WritableSignal } from '@angular/core';

import { DialogService, SuiDialogRef } from '@sui/dialog';

import { environment } from 'src/environments/environment';
import {
  I18nKeyRejectedDialogComponent,
  I18nKeyRejectedDialogData,
  I18nKeyRejection,
} from './components/i18n-key-rejected-dialog/i18n-key-rejected-dialog.component';

const HTTP_BAD_REQUEST = 400;
const REJECTION_FALLBACK_MESSAGE =
  'The backend rejected this translation key (HTTP 400) without a message.';

@Injectable({
  providedIn: 'root',
})
export class MissingTranslationService implements MissingTranslationHandler {
  // ! Only DI-cycle-safe dependencies belong in the constructor: ngx-translate
  // ! instantiates this handler while building TranslateService, so anything
  // ! that (transitively) needs TranslateService is resolved lazily instead.
  // ! UtilsApiService is safe - it depends on HttpClient only.
  constructor(
    private utilsApi: UtilsApiService,
    private injector: Injector,
  ) {}

  handleKeys = {};

  private rejections: WritableSignal<I18nKeyRejection[]> = signal([]);
  private rejectionDialogRef: SuiDialogRef<unknown, void> | null = null;

  /**
   * Registers a key the runtime could not resolve. `source` (the current SPA
   * route) is stored as provenance so the periodic unused-key audit can tell
   * where a leftover key came from - see .claude/docs/i18n.md (Cleanup).
   *
   * Only key-shaped strings are worth reporting: anything with whitespace is
   * user data leaked into the translate pipeline (object names, error texts),
   * not a translation key - reporting it would pollute the keyword table.
   */
  private isKeyShaped(key: string): boolean {
    return !/\s/.test(key);
  }

  handle(params: MissingTranslationHandlerParams) {
    const translationKey = params.key;

    if (
      this.isKeyShaped(translationKey) &&
      typeof this.handleKeys[translationKey] === 'undefined'
    ) {
      this.handleKeys[translationKey] = true;

      this.utilsApi
        .reportMissingTranslation(translationKey, window.location.pathname)
        .pipe(
          catchError((error: unknown) => {
            this.reportRejection(translationKey, error);
            return of('Error, could not save missing translation :-(');
          }),
        )
        .subscribe();
    }

    return params.key;
  }

  /**
   * A 400 from `POST /utils/i18n` means the backend refused the key itself
   * (dot-prefix collision with a registered key, or whitespace) - a developer
   * bug the app cannot recover from (SEN-5188), so it is surfaced loudly in
   * development. Every other failure (network, 5xx) stays silent as before.
   */
  private reportRejection(translationKey: string, error: unknown): void {
    // SensikaHttpInterceptor rethrows a normalized `{ httpCode, message, ... }`
    // object, not the HttpErrorResponse; the raw shape is kept as a fallback.
    const normalized = (error ?? {}) as {
      httpCode?: number;
      message?: unknown;
    };
    const status =
      error instanceof HttpErrorResponse ? error.status : normalized.httpCode;

    if (status === HTTP_BAD_REQUEST) {
      const backendMessage =
        error instanceof HttpErrorResponse
          ? error.error?.message
          : normalized.message;
      const message =
        typeof backendMessage === 'string' && backendMessage.length > 0
          ? backendMessage
          : REJECTION_FALLBACK_MESSAGE;

      console.error('[i18n] ' + message);

      if (!environment.production) {
        this.showRejectionDialog({
          key: translationKey,
          message,
          route: window.location.pathname,
        });
      }
    }
  }

  /**
   * One dialog per page load: rejections arriving while it is open are
   * appended to its list instead of stacking modals; closing it resets the
   * queue so a later rejection opens a fresh one.
   */
  private showRejectionDialog(rejection: I18nKeyRejection): void {
    this.rejections.update((list) => [...list, rejection]);

    if (this.rejectionDialogRef === null) {
      const dialogService = this.injector.get(DialogService);
      const data: I18nKeyRejectedDialogData = { rejections: this.rejections };

      this.rejectionDialogRef = dialogService.openSimpleDialog<
        I18nKeyRejectedDialogComponent,
        void
      >(I18nKeyRejectedDialogComponent, {
        data,
        role: 'alertdialog',
        closeOnClick: false,
        closeOnEscape: false,
      });

      this.rejectionDialogRef.closed.subscribe(() => {
        this.rejectionDialogRef = null;
        this.rejections.set([]);
      });
    }
  }
}

/**
 * CustomTranslateHttpLoader is a class that implements the TranslateLoader interface.
 * It is responsible for loading translations from the server.
 */
export class CustomTranslateHttpLoader implements TranslateLoader {
  constructor(private utilsApi: UtilsApiService) {}

  /**
   * Retrieves the translation for the specified language from the server.
   * @param lang The language code for the translation.
   * @returns An Observable that emits the translation data.
   */
  getTranslation(lang: string): Observable<any> {
    return this.utilsApi
      .getTranslation(lang)
      .pipe(catchError(() => of('Error, could not load translation :-(')));
  }
}
